import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import { getMauiIdFromUrl } from '@atlassian/malleable-ui-components/editor-remix/remix-ids';
import type { RemixHistoryEntryForUpdate } from '@atlassian/malleable-ui-components/types';

import { remixHistoryPluginKey } from './remix-history-plugin';

export function getHistoryEntryForUpdate({
	state,
	url,
	fieldId,
}: {
	fieldId?: string;
	state: EditorState;
	url?: string;
}): RemixHistoryEntryForUpdate | undefined {
	const remixHistoryState = remixHistoryPluginKey.getState(state);
	const historyMap = remixHistoryState?.historyMap;
	if (!historyMap) {
		return;
	}

	// The lookup URL is the live URL on the node. The stack entry URL is what
	// was synthesized when the entry was pushed. They may share a MAUI app id
	// but disagree on origin (e.g. BE-driven updates that re-use the node's
	// pre-update origin vs. user-driven inserts that use window.location.origin),
	// so compare by extracted app id rather than full-string equality.
	const targetAppId = getMauiIdFromUrl(url);

	for (const historyEntry of Object.values(historyMap)) {
		const currentStackEntry = historyEntry.stack.find(
			(stackEntry) =>
				(fieldId && stackEntry.fieldId === fieldId) ||
				(url && stackEntry.url === url) ||
				(targetAppId && getMauiIdFromUrl(stackEntry.url) === targetAppId),
		);
		if (!currentStackEntry) {
			continue;
		}

		const historyEntryForUpdate: RemixHistoryEntryForUpdate = {
			conversationId: historyEntry.conversationId,
			dynamicUiSource: historyEntry.dynamicUiSource,
			latestFinalMessage: historyEntry.latestFinalMessage,
			latestFinalMessageId: historyEntry.latestFinalMessageId,
			dynamicUiType: currentStackEntry.dynamicUiType,
			dynamicUiSubtype: currentStackEntry.dynamicUiSubtype,
			finalMessage: currentStackEntry.finalMessage,
			finalMessageId: currentStackEntry.finalMessageId,
			fieldId: currentStackEntry.fieldId,
			url: currentStackEntry.url,
		};

		return historyEntryForUpdate;
	}
}
